import type { ReactNode } from "react";
import { cn } from "../../utils";
import { CardList } from "../card-list";

export interface SettingsSectionProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned controls in the header (e.g. "Add new" button). */
  actions?: ReactNode;
  /** Numeric tally → the neutral Count pill beside the title. */
  count?: number;
  /** Section anchor for deep-linking (e.g. id="pipelines" → /…#pipelines). */
  id?: string;
  /** Extra classes on the outer Card. */
  className?: string;
  /** Extra classes on the body wrapper (e.g. drop padding for a flush table). */
  bodyClassName?: string;
  children: ReactNode;
}

/**
 * A titled settings card — the standard section wrapper inside a settings
 * page. Delegates its Card + header chrome to the one flush-card recipe
 * ({@link CardList}, VERTICAL-RHYTHM-PRD §T3), whose header is the canonical
 * `SectionTitle variant="card"` — a title row with a count pill, an optional
 * right-aligned action, and a **hairline divider beneath it** — over a
 * `px-4 py-4` body.
 *
 * This makes a settings section look identical to a dashboard card: same title
 * component, same weight, same divider. The body shares the header's `px-4`
 * gutter (the R7 fix — it was `px-6`, misaligning the title and body by 8px on
 * every non-table settings card). `title`→title, `description`→subtitle,
 * `actions`→action, `count`→count.
 */
export function SettingsSection({
  title,
  description,
  actions,
  count,
  id,
  className,
  bodyClassName,
  children,
}: SettingsSectionProps) {
  return (
    <CardList
      id={id}
      title={title}
      subtitle={description}
      action={actions}
      count={count}
      className={cn(id && "scroll-mt-24", className)}
    >
      {/* Body on the px-4 header gutter (py-4 unchanged). `bodyClassName` still
          drops padding for a flush table (it lands after px-4 py-4, so it wins). */}
      <div className={cn("px-4 py-4", bodyClassName)}>{children}</div>
    </CardList>
  );
}
